import type { CollabEnvelope } from '../types/envelope.js';
import type { DispatchReceipt } from '../types/dispatch.js';
import type { HtsUri } from '../types/uri.js';
import type {
  LinkAdapter,
  MeshTransportBus,
  SubscriptionHandler,
} from '../types/transport.js';
import { Deduplicator } from './deduplicator.js';

interface QueuedPacket {
  destinationUri: HtsUri;
  envelope: CollabEnvelope<unknown>;
}

function estimateEnvelopeSize(
  envelope: CollabEnvelope<unknown>,
): number {
  return JSON.stringify(envelope).length;
}

function baseReceipt(
  envelope: CollabEnvelope<unknown>,
): DispatchReceipt {
  return {
    eventId: envelope.eventId,
    accepted: true,
    queued: false,
    timestamp: Date.now(),
  };
}

export class DefaultMeshBus implements MeshTransportBus {
  private readonly links: LinkAdapter[] = [];
  private readonly queuedPackets: QueuedPacket[] = [];
  private readonly deduplicator: Deduplicator;
  private readonly subscriptions = new Map<
    string,
    Set<SubscriptionHandler>
  >();

  constructor(maxDedupEntries = 1000) {
    this.deduplicator = new Deduplicator(maxDedupEntries);
  }

  registerLink(adapter: LinkAdapter): void {
    if (this.links.some((link) => link.kind === adapter.kind)) {
      throw new Error(
        `Link kind already registered: ${adapter.kind}`,
      );
    }

    if (!Number.isInteger(adapter.mtu) || adapter.mtu <= 0) {
      throw new Error('Link MTU must be a positive integer');
    }

    this.links.push(adapter);
  }

  async dispatch(
    destinationUri: HtsUri,
    envelope: CollabEnvelope<unknown>,
  ): Promise<DispatchReceipt> {
    if (destinationUri !== envelope.destinationUri) {
      return {
        eventId: envelope.eventId,
        accepted: false,
        queued: false,
        timestamp: Date.now(),
      };
    }

    const receipt = await this.tryDispatch(
      destinationUri,
      envelope,
    );

    if (receipt !== undefined) {
      return receipt;
    }

    this.queuedPackets.push({
      destinationUri,
      envelope,
    });

    return {
      ...baseReceipt(envelope),
      queued: true,
    };
  }

  async flushQueued(): Promise<DispatchReceipt[]> {
    const pending = this.queuedPackets.splice(
      0,
      this.queuedPackets.length,
    );

    const receipts: DispatchReceipt[] = [];

    for (const packet of pending) {
      const receipt = await this.tryDispatch(
        packet.destinationUri,
        packet.envelope,
      );

      if (receipt !== undefined) {
        receipts.push(receipt);
      } else {
        this.queuedPackets.push(packet);
        receipts.push({
          ...baseReceipt(packet.envelope),
          queued: true,
        });
      }
    }

    return receipts;
  }

  subscribe(
    resourceUri: HtsUri,
    handler: SubscriptionHandler,
  ): () => void {
    const handlers =
      this.subscriptions.get(resourceUri) ??
      new Set<SubscriptionHandler>();

    handlers.add(handler);
    this.subscriptions.set(resourceUri, handlers);

    return () => {
      const current = this.subscriptions.get(resourceUri);

      if (current === undefined) {
        return;
      }

      current.delete(handler);

      if (current.size === 0) {
        this.subscriptions.delete(resourceUri);
      }
    };
  }

  async receive(
    envelope: CollabEnvelope<unknown>,
  ): Promise<void> {
    if (envelope.routing !== undefined) {
      if (envelope.routing.hopLimit <= 0) {
        return;
      }

      if (envelope.routing.expiresAt <= Date.now()) {
        return;
      }
    }

    if (this.deduplicator.isDuplicate(envelope.eventId)) {
      return;
    }

    const matchedHandlers = new Set<SubscriptionHandler>();

    // 1. Exact matches
    const exact = this.subscriptions.get(envelope.destinationUri);
    if (exact) {
      for (const h of exact) matchedHandlers.add(h);
    }

    // 2. Wildcard matches (e.g. hts://mesh/chat/*)
    for (const [subUri, handlers] of this.subscriptions.entries()) {
      if (subUri.endsWith('*')) {
        const prefix = subUri.slice(0, -1);
        if (envelope.destinationUri.startsWith(prefix)) {
          for (const h of handlers) matchedHandlers.add(h);
        }
      }
    }

    if (matchedHandlers.size === 0) {
      return;
    }

    const handlerResults = [...matchedHandlers].map(
      (handler) => Promise.resolve(handler(envelope)),
    );

    await Promise.allSettled(handlerResults);
  }

  private async tryDispatch(
    destinationUri: HtsUri,
    envelope: CollabEnvelope<unknown>,
  ): Promise<DispatchReceipt | undefined> {
    const size = estimateEnvelopeSize(envelope);

    const candidates = this.links
      .filter((link) => link.isAvailable())
      .filter((link) => link.mtu >= size)
      .sort(
        (left, right) =>
          Number(right.reliable) - Number(left.reliable),
      );

    for (const link of candidates) {
      try {
        await link.send(envelope);

        const receipt: DispatchReceipt = {
          ...baseReceipt(envelope),
          selectedLink: link.kind,
        };

        return receipt;
      } catch {
        // Try the next available candidate.
      }
    }

    return undefined;
  }
}
